import useTheme from '@mui/material/styles/useTheme';
import { CXAvatar } from 'common/components/cx-avatar/cx-avatar';
import { DrawerHeader } from 'common/components/material/Drawer';
import Typography from 'common/components/material/Typography';
import { ButtonTabs, CloseButton } from 'common/components/navigation';
import useDrawer from 'common/hooks/useDrawer';
import React, { useState } from 'react';
import {
  DrawerTitle,
  faintScrollbarStyles,
  FeedbackList,
} from 'features/matching-engine/matching-engine.utils';
import { WorkItemsForSkill } from 'features/matching-engine/components/MatchingEngineComponents/ProfileSkillBreakdown';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { useGetProfile } from 'features/profiles';
import { Skill } from 'features/skills';
import { EndorsementSection } from './endorsement-section';
import { useGetPastSkillFeedbacks } from 'features/matching-engine/hooks/useGetPastSkillFeedback';
import { Box, Stack } from 'common/components/material';

export enum SkillFeedbackDrawerTabs {
  Activities = 'activities',
  Feedback = 'feedback',
}

const SKILL_FEEDBACK_DETAILS_DRAWER_TABS = [
  {
    value: SkillFeedbackDrawerTabs.Activities,
    label: 'Activities',
  },
  {
    value: SkillFeedbackDrawerTabs.Feedback,
    label: 'Feedback',
  },
];

export const SkillFeedbackDrawer: React.FC<{
  profileId: string;
  skill: Skill;
  initialTab: SkillFeedbackDrawerTabs;
  groupId?: string;
  onClose?: () => {};
}> = ({ profileId, skill, initialTab, groupId, onClose = () => {} }) => {
  const theme = useTheme();
  const { data: profile } = useGetProfile({
    id: profileId,
  });
  const { closeDrawer } = useDrawer();
  const fullName = profile?.fullName;
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const closeCurrentDrawer = () => {
    closeDrawer(DrawerIdEnum.SKILL_FEEDBACK_DRAWER);
    onClose();
  };

  const { data: skillFeedbacks, refetch } = useGetPastSkillFeedbacks(profileId, skill.id);
  const feedbacks = skillFeedbacks?.pastFeedbacks ?? [];
  return (
    <>
      <DrawerHeader>
        <Stack flexDirection="column" spacing={2} pl={2} pt={2} width="100%">
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
            <Stack flexDirection="row" alignItems="center">
              <CXAvatar src={profile?.photo} alt={profile?.fullName} />
              <DrawerTitle fontSize={'1.45rem'} ml={2}>
                {fullName}
              </DrawerTitle>
            </Stack>

            <CloseButton clickHandler={closeCurrentDrawer} />
          </Stack>
          <Typography variant="body1" fontWeight={550}>
            {skill.name}
          </Typography>
        </Stack>
      </DrawerHeader>

      <Box
        sx={{
          ...faintScrollbarStyles(theme),
          height: 'calc(100% - 72px)',
          overflowY: 'auto',
          px: 3,
          mt: 3,
        }}
      >
        <EndorsementSection
          onSubmit={() => refetch()}
          skill={skill}
          revieweeId={profileId}
          groupId={groupId}
        />
        <ButtonTabs
          selectedValues={[selectedTab]}
          tabs={SKILL_FEEDBACK_DETAILS_DRAWER_TABS}
          onChange={setSelectedTab}
          scroll
          sx={{
            px: { xs: 1.5, md: 0 },
            py: 2,
            gap: 0.75,
            mt: 2,
          }}
        />
        {(() => {
          switch (selectedTab) {
            case SkillFeedbackDrawerTabs.Activities:
              return <WorkItemsForSkill skillId={skill.id} profileId={profileId} />;
            case SkillFeedbackDrawerTabs.Feedback:
              return <FeedbackList feedbacks={feedbacks} />;
            default:
              return null;
          }
        })()}
      </Box>
    </>
  );
};
